import { MutableRefObject } from 'react'
import voiceoverObserver, {
  PAGE_TYPE_CHANGE,
  PAGE_INFO_CHANGE,
  PAGE_AUDIO_STOP,
  PAGE_AUDIO_ENDED,
} from 'app/services/voiceover/Voiceover.observer'
import { EBookLocation } from 'app/types/ebook'
import { annotationController } from '../annotation/annotation-controller.service'

/**
 * iFrameId is in format xxx-xxx-xxx-yyy, where xxx - any not unique string, yyy - unique number which identify current iFrame
 * there is a bug in epub reader lib with replacement of ) and ( characters in id with other characters
 * this function will return just unique part of Id for comparison
 */
// TODO do this modification across all voiceover sources
function parseIdNumberFromIframeId(iframeId: string) {
  return iframeId.substring(iframeId.lastIndexOf('-') + 1)
}

const highlightedColor = '#008a12'
const doublehighlightedColor = '#22622b'
const defaultColor = '#3b3b3b'

type Word = {
  startTime: string
  endTime: string
  bodyId: string
  spanIds: string[]
  toCheck: boolean
  word: string
}

type WordsData = Record<string, Word[]>

/**
 * Service that is used to work with <AudioPlayer> timeupdate event and <EReader> ref
 * for voiceover functionality implementation
 */
export default class VoiceOverService {
  private static instance: VoiceOverService
  private wordsData: Record<string, Word[]> = {}
  private activeElementsStyle: Record<
    string,
    {
      background: string
      color: string
    }
  > = {}
  private wordsDataPageChunk: Word[] = []
  private activeWord: Word | null = null
  private isAudioPage: boolean = false
  private audioEndTime: number | null = null
  private location: string[] = []
  private pageIds: string[] = []
  private activeIframePage: HTMLIFrameElement | null = null
  private activeDomElements: (HTMLElement | null | undefined)[] = []
  private previousActiveElements: (HTMLElement | null | undefined)[] = []
  public audioStartTime: number | null = null

  /**
   * @param {Array} wordsData - array of words timings with dom elements ID's bindings
   */
  constructor(wordsData: WordsData = {}) {
    if (VoiceOverService.instance) {
      // this return is required for correct display of audio play progress
      // eslint-disable-next-line no-constructor-return
      return VoiceOverService.instance
    }

    VoiceOverService.instance = this
    this.wordsData = wordsData

    this.activeElementsStyle = {}

    // this return is required for correct display of audio play progress
    // eslint-disable-next-line no-constructor-return
    return this
  }

  /**
   * Update wordsData in service
   * @param {Array} wordsData - array of words timings with dom elements ID's bindings
   */
  setWordsData(wordsData: Word[]) {
    if (!wordsData) return

    const sortedWordsData = wordsData.sort((a, b) => +a.endTime - +b.endTime)
    const wordsDataObject: Record<string, Word[]> = {}
    sortedWordsData.map((word) => {
      if (wordsDataObject[word.bodyId]) {
        wordsDataObject[word.bodyId].push(word)
      } else {
        wordsDataObject[word.bodyId] = [word]
      }
      return word
    })
    this.wordsData = wordsDataObject
  }

  /**
   * Finds spelled word in wordsData array by current audio time.
   * Please note that word.startTime and word.endTime are strings that we convert in to numbers during comparison
   * @param {Object} audioEl - audio element
   * @returns {void}
   */
  setActiveWordByTime(audioEl: HTMLAudioElement): void {
    const { currentTime } = audioEl
    const [activeWord] = this.wordsDataPageChunk.filter(
      (word) => +word.startTime <= currentTime && +word.endTime >= currentTime
    )
    this.activeWord = activeWord
  }

  /**
   * Get current location IDs
   * @param {Object} location - location info accessible in e-reader locationChanged callback
   * @returns {Array} - array of current locations Ids with 0, 1 or 2 items
   */
  parsePageIdsFromLocation(location: EBookLocation): string[] {
    // expected match example "GK_U08_RDR_2ndED17_Final-20170324-2"
    const results = [
      decodeURI(location.start.href.split('.')[0]),
      decodeURI(location.end.href.split('.')[0]),
    ]

    // flat array of unique, non-false, values
    this.location = [...new Set(results.filter((item) => Boolean(item)).flat())]
    return this.location
  }

  /** Check if page have words to highlight
   *  @param {Array} wordsChunk - this.wordsDataPageChunk
   */
  setIsAudioPageByWords(wordsChunk: Word[]) {
    this.isAudioPage = !!wordsChunk.length
    voiceoverObserver.notify(PAGE_TYPE_CHANGE, this.isAudioPage)
  }

  /**
   * Set list of words by locationIds parsed from location
   * @param {Object} location - location info accessible in e-reader locationChanged callback
   */
  setWordsDataChunkByLocation(location: EBookLocation) {
    this.pageIds = this.parsePageIdsFromLocation(location)
    if (this.pageIds.length > 1) {
      this.wordsDataPageChunk = [
        ...(this.wordsData[this.pageIds[0]] || []),
        ...(this.wordsData[this.pageIds[1]] || []),
      ]
    } else {
      this.wordsDataPageChunk = this.wordsData[this.pageIds[0]] || []
    }
  }

  /**
   * Finds first word on a page starting time
   * Used to set correct time during navigation
   * @returns {number} - time of first sound in current wordsDataPageChunk
   */
  setAudioStartTimeByLocation(): void {
    this.audioStartTime = this.wordsDataPageChunk.length
      ? parseFloat(this.wordsDataPageChunk[0].startTime)
      : null
  }

  /**
   * Finds first word on a page starting time
   * Used to set correct time during navigation
   * @returns {number} - time of first sound in current wordsDataPageChunk
   */
  setAudioEndTimeByLocation(): void {
    this.audioEndTime = this.wordsDataPageChunk.length
      ? parseFloat(
          this.wordsDataPageChunk[this.wordsDataPageChunk.length - 1].endTime
        )
      : null
  }

  /**
   * Check if we reached to words end on the page
   * @param {Object} audioEl - audio element
   * @returns {boolean}
   */
  // TODO resolve race condition with audioEl.play() and audioEl.pause() here when audio isn't loaded to audioEl.currentTime
  audioShouldStop(audioEl: HTMLAudioElement): boolean {
    if (
      this.wordsDataPageChunk.length === 0 &&
      this.audioStartTime === 0 &&
      this.audioEndTime === 0
    ) {
      return false
    }
    if (this.wordsDataPageChunk.length) {
      const lastPageWordEndTime =
        this.wordsDataPageChunk[this.wordsDataPageChunk.length - 1].endTime

      if (this.audioStartTime && audioEl.currentTime < this.audioStartTime) {
        return false
      }

      if (+lastPageWordEndTime < audioEl.currentTime)
        voiceoverObserver.notify(PAGE_AUDIO_ENDED, true)

      const shouldStop =
        !this.wordsDataPageChunk.length ||
        this.activeIframePage === null ||
        +lastPageWordEndTime < audioEl.currentTime

      if (!shouldStop) return false
    }
    voiceoverObserver.notify(PAGE_AUDIO_STOP, true)
    return true
  }

  /**
   * Sets iframe in this.activeIframePage that has active word and will be used in words highlighting
   * @param {Object} readerRef - ref to e-reader that we get from getRef prop callback
   */
  setActiveIframeBody(readerRef: MutableRefObject<HTMLDivElement | null>) {
    if (!this.activeWord) return
    const { bodyId } = this.activeWord
    const iframes = readerRef.current?.getElementsByTagName('iframe')
    const [matchedIframe] = [...(iframes ?? [])].filter((iframe) => {
      const iframeBody = iframe.contentWindow?.document.querySelector('body')
      const iframeBodyId = iframeBody ? iframeBody.id : null
      return iframeBodyId
        ? parseIdNumberFromIframeId(iframeBodyId) ===
            parseIdNumberFromIframeId(bodyId)
        : false
    })

    this.activeIframePage = matchedIframe
  }

  /**
   * Pause player
   * @param {Object} audioEl - audio element
   */
  // eslint-disable-next-line class-methods-use-this
  pausePlayer(audioEl: HTMLAudioElement) {
    if (audioEl) {
      audioEl.pause()
    }
  }

  /**
   * Sets list of spans in this.activeDomElements to be highlighted a
   * nd this.previousActiveElements to be un-highlighted on the next tick()
   */
  setActiveDomElements() {
    if (!this.activeWord) return

    const { spanIds } = this.activeWord
    const activeIframeDoc = this.activeIframePage?.contentWindow?.document

    this.activeDomElements = spanIds.map((id) =>
      activeIframeDoc?.getElementById(id)
    )
    this.previousActiveElements = this.activeDomElements
  }

  /**
   * Un-highlight previous active word.
   */
  removePreviousActiveElementsHighlight() {
    if (this.previousActiveElements && this.previousActiveElements.length) {
      this.previousActiveElements.forEach((el) => {
        if (!el) return

        if (this.activeElementsStyle[el.id]) {
          el.style.setProperty('color', this.activeElementsStyle[el.id].color)
        } else {
          el.style.setProperty('color', defaultColor)
        }
        el.style.setProperty('text-decoration', 'none')
      })
    }
  }

  /**
   * Highlight dom elements that are pronounced by the audio-player at tha moment
   */
  highlightActiveDomElements() {
    this.removePreviousActiveElementsHighlight()
    this.setActiveDomElements()
    this.activeDomElements.forEach((el) => {
      if (!el) return

      const color = annotationController.highlightedSpans?.[el.id]
        ? doublehighlightedColor
        : highlightedColor
      el.style.setProperty('text-decoration', `underline dashed ${color}`)
      el.style.setProperty('text-underline-offset', '0.2em')
      el.style.setProperty('color', color)
    })
  }

  /** Clears all highlights, used on reader reset btn */
  clearAllHighlights() {
    if (this.previousActiveElements) {
      this.previousActiveElements.forEach((el) => {
        if (!el) return

        el.style.setProperty('text-decoration', 'none')
        el.style.setProperty('color', defaultColor)
      })
    }
    if (this.activeDomElements) {
      this.activeDomElements.forEach((el) => {
        if (!el) return

        el.style.setProperty('color', defaultColor)
      })
    }
  }

  /**
   * Function that should be attached to ereader locationChanged callback
   * Used to update words data chunk when page changes
   * @param location
   */
  onLocationChange(location: EBookLocation) {
    this.setWordsDataChunkByLocation(location)
    this.setIsAudioPageByWords(this.wordsDataPageChunk)
    this.setAudioStartTimeByLocation()
    this.setAudioEndTimeByLocation()

    const pageInfo = {
      isAudioPage: this.isAudioPage,
      pageStartTime: this.audioStartTime,
      pageEndTime: this.audioEndTime,
    }

    voiceoverObserver.notify(PAGE_INFO_CHANGE, pageInfo)
  }

  /**
   * Function that initializes all-in-one audio for the book,
   * audio which can be started from the beginning on every page
   */
  initAllInOneAudio() {
    this.isAudioPage = true
    this.audioStartTime = 0
    this.audioEndTime = 0

    const pageInfo = {
      isAudioPage: this.isAudioPage,
      pageStartTime: this.audioStartTime,
      pageEndTime: this.audioEndTime,
    }

    voiceoverObserver.notify(PAGE_TYPE_CHANGE, true)
    voiceoverObserver.notify(PAGE_INFO_CHANGE, pageInfo)
  }

  saveActiveDomElementStyles() {
    this.activeElementsStyle = {}
    this.activeDomElements.forEach((el) => {
      if (!el) return

      this.activeElementsStyle[el.id] = {
        background: el.style.background,
        color: el.style.color,
      }
    })
  }

  getCurrentPageIds() {
    return this.pageIds
  }

  getEventPageID() {
    const pageNums: string[] = []
    this.pageIds.forEach((pageId) => {
      pageNums.push(pageId.slice(pageId.lastIndexOf('-') + 1))
    })

    return parseInt(pageNums[0], 10)
  }

  /**
   * Function that should be attached to audio timeupdate event.
   * Used to run voice-over
   * @param {Object} audioEl - audio element
   * @param {Object} readerRef - ref to ereader that we get from getRef prop callback
   */
  tick(
    audioEl: HTMLAudioElement,
    readerRef: MutableRefObject<HTMLDivElement | null>,
    disableHighlighting = false
  ) {
    this.setActiveWordByTime(audioEl)
    this.setActiveIframeBody(readerRef)

    if (this.audioShouldStop(audioEl)) {
      this.pausePlayer(audioEl)
      return
    }

    // Go to next tick in case there is no word on current timing
    if (!this.activeWord) return

    this.removePreviousActiveElementsHighlight()
    this.setActiveDomElements()
    this.saveActiveDomElementStyles()
    if (!disableHighlighting) {
      this.highlightActiveDomElements()
    }
  }
}
